import React, { FC, Ref } from 'react';
import styled from 'styled-components';

import BetterBusinessBureauSvg from '../../assets/icons/seals/better-business-bureau.svg';
import EqualHousingLenderSvg from '../../assets/icons/seals/equal-housing-lender-logo.svg';
import FacebookSvg from '../../assets/icons/social/facebook.svg';
import InstagramSvg from '../../assets/icons/social/instagram.svg';
import LinkedInSvg from '../../assets/icons/social/linkedin.svg';
import TwitterSvg from '../../assets/icons/social/twitter.svg';
import {
  CARD_BREAKPOINT,
  COASTAL_BANK_PRIVACY_POLICY_URL,
  colors,
  LegalDocuments,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from '../constants';
import { getUrlForLegalDoc } from '../data/graphql';
import { useAnnualFee } from '../utilities/useAnnualFee';

import FileDownloadLink from './FileDownloadLink';

// Footer
// Displays additional information at the bottom of a page

interface FooterProps {
  postOffer?: boolean;
  // Essentially a manual React.forwardRef() that supports multiple refs
  footnoteRef?: Array<Ref<HTMLInputElement>>;
  conditionalOnCashflowVerification?: boolean;
  onApplyPage?: boolean;
  onNonFunnelPage?: boolean;
}

interface StyledProps {
  isOnApply?: boolean;
}

// #region -- Styles ------------------------------------------------------------------------------
const FooterBackground = styled.div`
  width: 100%;
  margin: 4.6875rem 0 0;
  background: #ffffff;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 1.5rem 0 0;
  }
`;
FooterBackground.displayName = 'FooterBackground';

const Container = styled.footer`
  max-width: 57rem;
  margin: 0 auto;
  padding: 1.75rem 1.5rem 6.25rem;
  color: ${colors.TEXT_LABEL};
  line-height: 1.5rem;
  font-size: 0.875rem;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    padding-top: 0;
  }
`;
Container.displayName = 'Container';

const Section = styled.section`
  border-bottom: 0.0625rem solid ${colors.BORDER_PRIMARY};
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 2.75rem 0 2.75rem;
  &:last-of-type {
    border-bottom: none;
  }
`;
Section.displayName = 'Section';

const Social = styled.div`
  width: 17rem;
  height: 3.125rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;
Social.displayName = 'Social';

const SocialLink = styled.a`
  svg {
    &:hover {
      .logo {
        fill: ${colors.TEXT_PRIMARY};
      }
      .circle {
        stroke: ${colors.TEXT_PRIMARY};
      }
    }
  }
`;
SocialLink.displayName = 'SocialLink';

const PrivacyLink = styled(FileDownloadLink)`
  color: ${colors.TEXT_LABEL};
  text-decoration: underline;
  &:hover,
  &:focus {
    color: ${colors.TEXT_PRIMARY};
  }
  &:focus:not(:hover, :focus-visible) {
    color: ${colors.TEXT_LABEL};
  }
`;
PrivacyLink.displayName = 'PrivacyLink';

const Facebook = styled(FacebookSvg)`
  width: 3.125rem;
`;
Facebook.displayName = 'Facebook';

const Twitter = styled(TwitterSvg)`
  width: 3.125rem;
`;
Twitter.displayName = 'Twitter';

const LinkedIn = styled(LinkedInSvg)`
  width: 3.125rem;
`;
LinkedIn.displayName = 'LinkedIn';

const Instagram = styled(InstagramSvg)`
  width: 3.125rem;
`;
Instagram.displayName = 'Instagram';

// Hidden component for superscript aria-describedby references
const FootnoteDescriber = styled.div`
  display: none;
`;
FootnoteDescriber.displayName = 'FootnoteDescriber';

const PrivacyNote = styled.p`
  font-weight: 400;
  line-height: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${colors.TEXT_LABEL};
`;
PrivacyNote.displayName = 'PrivacyNote';

const CardholderAgreementLink = styled.a`
  color: ${colors.TEXT_LABEL};
  text-decoration: underline;
`;
CardholderAgreementLink.displayName = 'CardholderAgreementLink';

const Certificates = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 26.125rem;
  margin-bottom: 2.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    flex-direction: column;
  }
`;
Certificates.displayName = 'Certificates';

const Seals = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 9.5rem;
`;
Seals.displayName = 'Seals';

const TextSpan = styled.span`
  padding: 0 0.75rem;
`;
TextSpan.displayName = 'TextSpan';

const EqualHousingLender = styled(EqualHousingLenderSvg)``;
EqualHousingLender.displayName = 'EqualHousingLender';

const BetterBusinessBureau = styled(BetterBusinessBureauSvg)``;
BetterBusinessBureau.displayName = 'BetterBusinessBureau';

const LicensingDisclosures = styled.a`
  border: 0.0625rem solid ${colors.BORDER_PRIMARY};
  border-radius: 6.25rem;
  text-decoration: none;
  color: ${colors.TEXT_LABEL};
  height: 3.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: 400;
  padding: 0 1.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-top: 1.5rem;
  }
  &:hover,
  &:focus {
    color: ${colors.TEXT_PRIMARY};
    border-color: ${colors.TEXT_PRIMARY};
  }
  &:focus:not(:hover, :focus-visible) {
    color: ${colors.TEXT_LABEL};
    border-color: ${colors.BORDER_PRIMARY};
  }
`;
LicensingDisclosures.displayName = 'LicensingDisclosures';

const Address = styled(PrivacyNote)``;
Address.displayName = 'Address';

const AddressLink = styled(PrivacyLink)``;
AddressLink.displayName = 'AddressLink';

const Copyright = styled(PrivacyNote)`
  margin-top: 1.5rem;
`;
Copyright.displayName = 'Copyright';

const FooterCopy = styled.p`
  width: 100%;
  font-size: 0.875rem;
  margin-bottom: 1.5625rem;
  line-height: 1.375rem;
  &:last-child {
    margin-bottom: 0;
  }
  color: ${colors.TEXT_LABEL};
`;
FooterCopy.displayName = 'FooterCopy';

const FootnoteItem = styled(FooterCopy)`
  position: relative;
`;
FootnoteItem.displayName = 'FootnoteItem';

// Allow footnote numbers to be clicked to return to origin content
export const ClickableSup = styled.a<StyledProps>`
  cursor: pointer;
  position: absolute;
  padding: 0 0 0 0.0625rem;
  color: ${colors.TEXT_LABEL};
  text-decoration: none;
  top: 0;
  left: 0;
  ${(p: StyledProps) =>
    p.isOnApply &&
    `
    position: relative;
    padding: 0 0.125rem 0 0;
    scroll-behavior: smooth;
  `};
`;
ClickableSup.displayName = 'ClickableSup';

const OptionalBreakPoint = styled.br`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
OptionalBreakPoint.displayName = 'OptionalBreakPoint';

const Sup = styled.sup`
  position: relative;
  top: 0.125rem;
  padding-left: 0.0625rem;
  font-size: 0.625rem;
`;
Sup.displayName = 'Sup';
// #endregion

export const onSuperscriptClick = (src: string) => {
  if (src && document) {
    const { top } = document.getElementById(src).getBoundingClientRect();
    window.scrollTo({ top, left: 0, behavior: 'smooth' });
  }
};

// Component --------------------------------------------------------------------------------------
export const Footer: FC<FooterProps> = ({
  postOffer,
  footnoteRef,
  conditionalOnCashflowVerification,
  onApplyPage,
  onNonFunnelPage,
}) => {
  const annualFee = useAnnualFee();

  return (
    <FooterBackground>
      <Container>
        <Section>
          <Social>
            <SocialLink
              href="https://facebook.com/prosperloans"
              aria-label="See Prosper on Facebook"
              target="_blank"
            >
              <Facebook />
            </SocialLink>
            <SocialLink
              href="https://twitter.com/prosperloans"
              aria-label="See Prosper on Twitter"
              target="_blank"
            >
              <Twitter />
            </SocialLink>
            <SocialLink
              href="http://www.linkedin.com/company/prosper-marketplace"
              aria-label="See Prosper on LinkedIn"
              target="_blank"
            >
              <LinkedIn />
            </SocialLink>
            <SocialLink
              href="https://www.instagram.com/letsgoprosper"
              aria-label="See Prosper on Instagram"
              target="_blank"
            >
              <Instagram />
            </SocialLink>
          </Social>
          <PrivacyNote>
            Prosper and Coastal Community Bank take your privacy seriously.{' '}
            <OptionalBreakPoint />
            Please see{' '}
            <PrivacyLink
              target="_blank"
              href={getUrlForLegalDoc(LegalDocuments.PROSPER_PRIVACY_POLICY)}
            >
              Prosper’s Privacy Policy
            </PrivacyLink>{' '}
            and{' '}
            <PrivacyLink target="_blank" href={COASTAL_BANK_PRIVACY_POLICY_URL}>
              Coastal Community Bank’s Privacy Policy
            </PrivacyLink>{' '}
            for more details.
          </PrivacyNote>
        </Section>
        {!onNonFunnelPage && (
          <Section>
            <FootnoteDescriber id="footnotes-describer">
              Footnotes
            </FootnoteDescriber>
            {onApplyPage && (
              <FooterCopy>
                <ClickableSup
                  id="footnote-1"
                  tabIndex={0}
                  onClick={() => onSuperscriptClick('footnote-source-1')}
                  aria-label="footnote-1 back to main content"
                  role="link"
                  isOnApply
                >
                  ¹
                </ClickableSup>
                Although checking your offer won’t affect your credit score, if
                you are approved, your credit report will reflect a hard credit
                posting.
              </FooterCopy>
            )}
            <FooterCopy>
              IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT.
              <br />
              To help the government fight the funding of terrorism and money
              laundering activities, Federal law requires all financial
              institutions to obtain, verify, and record information that
              identifies each person who opens an account.
            </FooterCopy>
            <FooterCopy>
              What this means for you: When you open an account, we will ask for
              your name, address, date of birth, and other information that will
              allow us to identify you. We may also ask to see your driver’s
              license or other identifying documents.
            </FooterCopy>
            {
              // If we're at the offer screen or beyond, add this offer-related info
              postOffer && (
                <>
                  {conditionalOnCashflowVerification && (
                    <FooterCopy>
                      *Pre-approved offer: Credit is not guaranteed and you will
                      be required to verify your eligibility. The Terms &amp;
                      Conditions of your pre-approved offer are not effective
                      until Prosper communicates a final approval to you.
                    </FooterCopy>
                  )}
                  {
                    // For two-step, only show these footnotes past the offer page
                    // in conjunction to the offer glance starting in the bank connect page
                    (!conditionalOnCashflowVerification ||
                      !window.location.pathname.includes('offer')) && (
                      <>
                        <FootnoteItem
                          ref={footnoteRef[0]}
                          id="footnote-detail-1"
                        >
                          &nbsp;&nbsp;Automatic reviews for credit line
                          increases: We will automatically review your account
                          for potential changes. Your credit line can increase
                          or decrease, or not change at all, depending on your
                          payment history and eligibility.
                          <ClickableSup
                            id="footnote-1"
                            href="#footnote-source-1"
                            aria-label="Footnote-1 back to main content"
                            role="link"
                          >
                            ¹
                          </ClickableSup>
                        </FootnoteItem>
                        <FootnoteItem
                          ref={footnoteRef[1]}
                          id="footnote-detail-2"
                        >
                          &nbsp;&nbsp;The annual fee for this credit card is $
                          {annualFee}, which will be waived for your first year
                          if you are approved and sign up for AutoPay before we
                          issue your first statement.&nbsp;
                          <CardholderAgreementLink
                            href={getUrlForLegalDoc(
                              LegalDocuments.PROSPER_CARD_CARDHOLDER_AGREEMENT
                            )}
                            target="_blank"
                          >
                            See details
                          </CardholderAgreementLink>
                          &nbsp;for additional information about rates, fees,
                          and other terms.
                          <ClickableSup
                            id="footnote-2"
                            href="#footnote-source-2"
                            aria-label="Footnote-2 back to main content"
                            role="link"
                          >
                            ²
                          </ClickableSup>
                        </FootnoteItem>
                      </>
                    )
                  }
                </>
              )
            }
            <FooterCopy last-child>
              The Prosper
              <Sup>®</Sup>
              &nbsp;Card is issued by Coastal Community Bank, Member FDIC. The
              Prosper
              <Sup>®</Sup>
              &nbsp;Card is issued pursuant to a license from Mastercard
              <Sup>®</Sup>
              &nbsp;International.
            </FooterCopy>
          </Section>
        )}
        {onNonFunnelPage && (
          <Section>
            <FooterCopy>
              The Prosper
              <Sup>®</Sup>
              &nbsp;Card is issued by Coastal Community Bank, Member FDIC. The
              Prosper
              <Sup>®</Sup>
              &nbsp;Card is issued pursuant to a license from Mastercard
              <Sup>®</Sup>
              &nbsp;International.
            </FooterCopy>
          </Section>
        )}
        <Section>
          <Certificates>
            <Seals>
              <EqualHousingLender />
              <BetterBusinessBureau />
            </Seals>
            <LicensingDisclosures
              target="_blank"
              href="/heloc/licensing-and-disclosures"
            >
              Licensing and Disclosures
            </LicensingDisclosures>
          </Certificates>
          <Address>
            Prosper Funding LLC
            <TextSpan>|</TextSpan>
            221 Main Street, Suite 300
            <TextSpan>|</TextSpan>
            San Francisco, CA 94105
          </Address>
          <Address>
            <AddressLink href="/contact-us">Contact Us</AddressLink>
            <TextSpan>|</TextSpan>
            <AddressLink
              target="_blank"
              href={getUrlForLegalDoc(
                LegalDocuments.TERMS_OF_USE_AND_ELECTRONIC_CONSENT
              )}
            >
              Terms of Use and Electronic Consent
            </AddressLink>
          </Address>
          <Copyright>
            © 2005-
            {new Date().getFullYear()} Prosper Funding LLC. All rights reserved.
          </Copyright>
        </Section>
      </Container>
    </FooterBackground>
  );
};

Footer.defaultProps = {
  postOffer: false,
  footnoteRef: [],
  conditionalOnCashflowVerification: false,
  onApplyPage: false,
  onNonFunnelPage: false,
};
